import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useView } from '../context/ViewContext';
import { useOrganisme } from '../context/OrganismeContext';
import { 
  BuildingStorefrontIcon, 
  ArrowLeftIcon,
  CalendarIcon,
  BanknotesIcon,
  ExclamationTriangleIcon,
  ClipboardDocumentListIcon,
} from '@heroicons/react/24/outline';

import CalendrierReservations from './miellerie/CalendrierReservations';
import SoldeDette from './miellerie/SoldeDette';

export default function Miellerie() {
  const { user } = useAuth();
  const { activeView } = useView();
  const { organismeActif } = useOrganisme();
  const [activeTab, setActiveTab] = useState('calendrier');
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isResponsable, setIsResponsable] = useState(false);
  /**
   * Accès page usager : souscription miellerie valide + adhésion AMAIR active,
   * ou super admin. Un admin simple sans souscription n'a pas accès.
   */
  const [canViewMiellerie, setCanViewMiellerie] = useState(false);
  const [accesBloqueDepuis, setAccesBloqueDepuis] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tab = params.get('tab');
    if (tab === 'solde' || tab === 'calendrier' || tab === 'mes-reservations') {
      setActiveTab(tab);
    }
  }, []);

  useEffect(() => {
    // Attendre que les contextes soient initialisés
    if (activeView && organismeActif !== undefined) {
      checkUserAccess();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeView, organismeActif, user]);

  const checkUserAccess = async () => {
    try {
      const token = localStorage.getItem('token');
      const currentYear = new Date().getFullYear();

      const userRoles = user?.roles || [];
      const isSuperAdmin = userRoles.includes('super_admin');

      let isResp = false;
      try {
        const { data } = await axios.get(
          `${process.env.REACT_APP_API_URL}/miellerie/responsables/mon-statut`,
          {
            headers: { Authorization: `Bearer ${token}` },
            params: { annee: currentYear },
          }
        );
        isResp = !!data.estResponsable;
      } catch (err) {
        isResp = false;
      }

      let miellerieService = null;
      try {
        const services = await axios.get(`${process.env.REACT_APP_API_URL}/services/my-services`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        /** Même logique que `usagerMiellerie` côté API + adhésion liée encore « actif ». */
        const statutsServiceMiellerieAccesUsager = ['en_attente_caution', 'actif'];

        miellerieService = services.data.find((s) => {
          if (s.typeService !== 'miellerie' || s.organisme !== 'AMAIR') return false;
          if (Number(s.annee) !== currentYear) return false;
          if (!statutsServiceMiellerieAccesUsager.includes(s.status)) return false;
          const adh = s.adhesion;
          if (!adh || typeof adh !== 'object') return false;
          if (adh.status !== 'actif') return false;
          return true;
        }) || null;
      } catch (err) {
        console.error('Erreur chargement services miellerie:', err);
      }

      setService(miellerieService);
      const isAdminAMAIR =
        userRoles.includes('admin') && (user?.organismes || []).includes('AMAIR');
      setIsResponsable(isSuperAdmin || isAdminAMAIR || (Boolean(miellerieService) && isResp));
      setCanViewMiellerie(Boolean(miellerieService) || isSuperAdmin);

      try {
        const { data: balanceData } = await axios.get(
          `${process.env.REACT_APP_API_URL}/miellerie/balance?organisme=AMAIR`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setAccesBloqueDepuis(balanceData?.balance?.accesBloqueDepuis || null);
      } catch {
        setAccesBloqueDepuis(null);
      }

      setLoading(false);
    } catch (err) {
      console.error('Erreur vérification accès:', err);
      setCanViewMiellerie(false);
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'calendrier', label: 'Réserver un créneau', icon: CalendarIcon },
    { id: 'mes-reservations', label: 'Mes réservations', icon: ClipboardDocumentListIcon },
    { id: 'solde', label: 'Mon solde', icon: BanknotesIcon },
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-honey-600"></div>
      </div>
    );
  }

  // Pas de souscription : message (sauf super admin)
  if (!canViewMiellerie) {
    return (
      <div className="min-h-screen">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Link to="/" className="inline-flex items-center text-honey-600 hover:text-honey-700 mb-8">
            <ArrowLeftIcon className="h-4 w-4 mr-2" />
            Retour à l'accueil
          </Link>

          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-honey-100 rounded-2xl mb-6">
              <BuildingStorefrontIcon className="h-8 w-8 text-honey-600" />
            </div>
            <h1 className="text-4xl font-bold text-slate-900 mb-4">
              La miellerie collective
            </h1>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Accédez à notre miellerie collective équipée
            </p>
          </div>

          <div className="bg-honey-50 border border-honey-200 rounded-xl p-8 text-center max-w-2xl mx-auto">
            <ExclamationTriangleIcon className="h-12 w-12 text-honey-600 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-honey-900 mb-2">
              Accès réservé
            </h2>
            <p className="text-honey-800 mb-4">
              L&apos;accès à cette page n&apos;est possible que pour les adhérents ayant une adhésion
              <strong> AMAIR active</strong> pour l&apos;année en cours et une souscription aux services de
              la miellerie <strong>pour cette même année</strong> (parcours validé : droit d&apos;usage payé ou
              attente de dépôt de caution, selon votre dossier).
            </p>
            <p className="text-honey-800/90 text-sm mb-6">
              Retrouvez l&apos;adhésion AMAIR et la souscription miellerie depuis votre espace adhérent.
            </p>
            <Link
              to="/mes-adhesions"
              className="inline-flex items-center px-6 py-3 bg-honey-600 text-white rounded-lg hover:bg-honey-700 transition-colors"
            >
              Retour vers les adhésions
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Bandeau caution : uniquement si l'usager a un enregistrement service et caution encore en attente
  const showCautionWarning = Boolean(service) && service.caution?.status === 'en_attente';

  return (
    <div className="h-full bg-gray-50">
      {/* Breadcrumb */}
      <div className="bg-white border-b border-gray-200 px-4 sm:px-8 py-4">
        <div className="flex items-center text-sm text-gray-500">
          <Link to="/dashboard" className="hover:text-honey-600">
            Espace personnel
          </Link>
          <svg className="mx-2 h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
          </svg>
          <span className="font-medium text-gray-900">Miellerie collective</span>
        </div>
      </div>

      {/* Header */}
      <div className="px-4 sm:px-8 py-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Miellerie Collective</h1>
            <p className="mt-2 text-sm text-gray-600">
              Réservez un créneau, suivez vos réservations et votre solde
            </p>
          </div>
          {isResponsable && (
            <Link
              to="/gestion-miellerie"
              className="inline-flex items-center gap-1.5 self-start text-sm font-medium text-honey-700 hover:text-honey-800"
            >
              Gérer le planning
              <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20" aria-hidden>
                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
              </svg>
            </Link>
          )}
        </div>
      </div>

        {/* Bandeau accès bloqué (dette non régularisée) */}
        {accesBloqueDepuis && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 mx-4 sm:mx-6">
            <div className="flex items-start gap-3">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-red-800 font-medium">Accès à la miellerie suspendu</p>
                <p className="text-red-700 text-sm mt-1">
                  Toute dette doit être réglée avant la fin de la miellée courante.
                  En cas de non-paiement, l&apos;accès n&apos;est plus disponible et les réservations
                  sont annulées. Régularisez votre solde pour retrouver l&apos;accès.
                </p>
                <button
                  type="button"
                  onClick={() => setActiveTab('solde')}
                  className="mt-2 text-sm font-semibold text-red-800 underline hover:text-red-900"
                >
                  Aller à Mon solde
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Bandeau caution */}
        {showCautionWarning && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 mx-4 sm:mx-6">
            <div className="flex items-start gap-3">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-red-800 font-medium">Caution non déposée</p>
                <p className="text-red-700 text-sm mt-1">
                  N'oubliez pas d'apporter votre chèque de caution de 300€ lors de votre première visite.
                  Vous pouvez réserver mais pensez à l'apporter le jour même.
                </p>
              </div>
            </div>
          </div>
        )}

      {/* Content */}
        <div className="px-4 sm:px-8 pb-8 space-y-6">

          {/* Onglets */}
          <div className="border-b border-gray-200 flex items-center justify-between">
            <nav className="-mb-px flex space-x-4 sm:space-x-8 overflow-x-auto">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 px-3 sm:px-6 py-3 sm:py-4 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${
                      activeTab === tab.id
                        ? 'border-honey-600 text-honey-600'
                        : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                    }`}
                  >
                    <Icon className="h-5 w-5" />
                    {tab.label}
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Contenu des onglets */}
          <div className="">
            {activeTab === 'calendrier' && <CalendrierReservations view="calendrier" />}
            {activeTab === 'mes-reservations' && <CalendrierReservations view="list" />}
            {activeTab === 'solde' && <SoldeDette />}
          </div>
        </div>
      </div>
  );
}
